const sentryDsn = import.meta.env.VITE_SENTRY_DSN?.trim();
type SentryClient = typeof import("@sentry/react");
type MonitoringMode = "minimal" | "full";

let sentryPromise: Promise<SentryClient | null> | null = null;
let sentryInitializedMode: MonitoringMode | null = null;
let monitoringUser: { id: string } | null = null;

function isMonitoringEnabled() {
  return Boolean(sentryDsn);
}

function loadSentry() {
  if (!isMonitoringEnabled()) return Promise.resolve(null);

  sentryPromise ??= import("@sentry/react").catch((error) => {
    console.warn("[monitoring] Sentry SDK could not be loaded", error);
    return null;
  });

  return sentryPromise;
}

async function ensureSentryInitialized(mode: MonitoringMode = "minimal") {
  const Sentry = await loadSentry();
  if (!Sentry) return null;

  if (sentryInitializedMode !== mode) {
    Sentry.init({
      dsn: sentryDsn,
      integrations:
        mode === "full"
          ? [
              Sentry.browserTracingIntegration(),
              Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
              }),
            ]
          : [],
      tracesSampleRate: mode === "full" ? 0.2 : 0,
      replaysSessionSampleRate: mode === "full" ? 0.05 : 0,
      replaysOnErrorSampleRate: mode === "full" ? 1.0 : 0,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION,
      beforeSend(event) {
        if (mode === "minimal") {
          delete event.user;
        }

        return event;
      },
    });
    sentryInitializedMode = mode;
  }

  return Sentry;
}

export function initClientMonitoring() {
  if (!isMonitoringEnabled()) return;
  void ensureSentryInitialized("minimal");
}

export function configureClientMonitoringConsent(options: {
  monitoringAllowed: boolean;
  analyticsAllowed: boolean;
}) {
  if (!isMonitoringEnabled()) return;

  const mode: MonitoringMode = options.monitoringAllowed ? "full" : "minimal";

  void ensureSentryInitialized(mode).then((Sentry) => {
    if (!Sentry) return;

    if (mode !== "full" || !monitoringUser) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: monitoringUser.id,
    });
  });
}

export function setMonitoringUser(user: { id: string }) {
  if (!isMonitoringEnabled()) return;
  monitoringUser = user;

  void ensureSentryInitialized(sentryInitializedMode ?? "minimal").then((Sentry) => {
    if (sentryInitializedMode !== "full") return;
    Sentry?.setUser({
      id: user.id,
    });
  });
}

export function clearMonitoringUser() {
  if (!isMonitoringEnabled()) return;
  monitoringUser = null;
  void ensureSentryInitialized(sentryInitializedMode ?? "minimal").then((Sentry) => {
    Sentry?.setUser(null);
  });
}

export function captureMonitoringException(
  error: unknown,
  context?: string,
  details?: Record<string, unknown>,
) {
  if (!isMonitoringEnabled()) return;

  void ensureSentryInitialized(sentryInitializedMode ?? "minimal").then((Sentry) => {
    Sentry?.withScope((scope) => {
      if (context) {
        scope.setTag("context", context);
      }

      if (details && Object.keys(details).length > 0) {
        scope.setContext("details", details);
      }

      Sentry.captureException(error);
    });
  });
}
